import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './apps/Authentication/authentication.service';
import { JetsonDeviceStatus } from './jetson-status.service';
import { NotificationItem } from './notifications.service';
import { Detection } from 'src/app/pages/apps/Detection/Detection';

export type SignalRConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface CameraStatusEvent {
  status: number;
  statusName: string;
  tenantId: number;
  timestamp: string;
  cameraId?: string;
  errorMessage?: string;
}

/** Valores del campo status en CameraHeartbeatEvent (sincronizados con alpr_pipeline.py). */
export const CAMERA_STATUS = {
  Unknown: 0,
  Stopped: 1,
  Running: 2,
  Reconnecting: 3,
} as const;

/** Telemetría en vivo de una cámara, publicada por el Engine cada ~3s. */
export interface CameraHeartbeatEvent {
  cameraId: string;
  status: number;
  fps: number;
  lastFrameAgeMs: number;
  frameCount: number;
  droppedFrames: number;
  timestamp: string;
}

/** Payload de `NewParkingIncident` (evasión / tolerancia). */
export interface ParkingIncidentSignalPayload {
  incidentId: string;
  plate: string;
  estimatedAmount: number;
  incidentType: number;
  status: number;
  exitEvidenceImageUrl?: string | null;
  exitPlateImageUrl?: string | null;
  operatorUserId?: string | null;
  cashBoxOpeningId?: string | null;
  isPostClosure: boolean;
}

export type OccupancyChangeType = 'Opened' | 'Closed' | 'Updated' | string;

export interface OccupancyChangedEvent {
  sessionId: string;
  change: OccupancyChangeType;
}

/**
 * Central service for managing the SignalR connection to the ParkingHub.
 * 
 * Handles:
 * - Connection lifecycle with automatic reconnection
 * - Token-based authentication (access_token via query string)
 * - Exposes typed observables for each event type:
 *   - DeviceStatusChanged: Jetson device online/offline transitions
 *   - NewNotification: New notifications for the tenant
 *   - NewDetection: New license plate detections
 * 
 * The server automatically assigns the client to its tenant group based on JWT claims.
 */
@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  private hubConnection: signalR.HubConnection | null = null;
  private readonly hubUrl = `${environment.apiBaseUrl}/hubs/parking`;

  // Connection state
  private connectionStateSubject = new BehaviorSubject<SignalRConnectionState>('disconnected');
  public connectionState$ = this.connectionStateSubject.asObservable();

  // Event streams
  private deviceStatusChangedSubject = new Subject<JetsonDeviceStatus>();
  private newNotificationSubject = new Subject<NotificationItem>();
  private newDetectionSubject = new Subject<Detection>();
  private cameraStatusChangedSubject = new Subject<CameraStatusEvent>();
  private cameraHeartbeatSubject = new Subject<CameraHeartbeatEvent>();
  private newParkingIncidentSubject = new Subject<ParkingIncidentSignalPayload>();
  private occupancyChangedSubject = new Subject<OccupancyChangedEvent>();

  /** Emitted when a device goes online or offline */
  public deviceStatusChanged$: Observable<JetsonDeviceStatus> = this.deviceStatusChangedSubject.asObservable();
  
  /** Emitted when a new notification is created for this tenant */
  public newNotification$: Observable<NotificationItem> = this.newNotificationSubject.asObservable();
  
  /** Emitted when a new license plate detection is processed */
  public newDetection$: Observable<Detection> = this.newDetectionSubject.asObservable();

  /** Emitted when a camera's operational status changes */
  public cameraStatusChanged$: Observable<CameraStatusEvent> = this.cameraStatusChangedSubject.asObservable();

  /** Heartbeat periódico por-cámara con telemetría (fps, lastFrameAgeMs, contadores). */
  public cameraHeartbeat$: Observable<CameraHeartbeatEvent> = this.cameraHeartbeatSubject.asObservable();

  /** Incidente de estacionamiento (evasión / tolerancia); incluye operatorUserId para filtrar en UI. */
  public newParkingIncident$: Observable<ParkingIncidentSignalPayload> =
    this.newParkingIncidentSubject.asObservable();

  /** Emitted when parking occupancy changes (session opened, closed, or updated) */
  public occupancyChanged$: Observable<OccupancyChangedEvent> =
    this.occupancyChangedSubject.asObservable();

  private isStarted = false;

  constructor(private authService: AuthenticationService) {}

  ngOnDestroy(): void {
    this.stop();
  }

  /**
   * Starts the SignalR connection. Safe to call multiple times - only connects once.
   * The server automatically assigns this client to the correct tenant group.
   */
  async start(): Promise<void> {
    if (this.isStarted) return;
    this.isStarted = true;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.authService.getAccessToken() || '',
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect({
        // Retry intervals: 0s, 2s, 5s, 10s, 30s, then every 30s
        nextRetryDelayInMilliseconds: (retryContext) => {
          const delays = [0, 2000, 5000, 10000, 30000];
          return retryContext.previousRetryCount < delays.length
            ? delays[retryContext.previousRetryCount]
            : 30000;
        }
      })
      .configureLogging(environment.production ? signalR.LogLevel.Warning : signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.registerEventHandlers();

    // Connection lifecycle events
    this.hubConnection.onreconnecting(() => {
      console.log('[SignalR] Reconnecting...');
      this.connectionStateSubject.next('reconnecting');
    });

    this.hubConnection.onreconnected(() => {
      console.log('[SignalR] Reconnected successfully');
      this.connectionStateSubject.next('connected');
    });

    this.hubConnection.onclose(() => {
      console.log('[SignalR] Connection closed');
      this.connectionStateSubject.next('disconnected');
      this.isStarted = false;
    });

    // Start connection
    await this.connect();
  }

  /**
   * Stops the SignalR connection and cleans up.
   */
  async stop(): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
      } catch (err) {
        console.warn('[SignalR] Error stopping connection:', err);
      }
      this.hubConnection = null;
    }
    this.isStarted = false;
    this.connectionStateSubject.next('disconnected');
  }

  /**
   * Returns true if the connection is currently active.
   */
  get isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  private async connect(): Promise<void> {
    if (!this.hubConnection) return;

    this.connectionStateSubject.next('connecting');
    try {
      await this.hubConnection.start();
      console.log('[SignalR] Connected to ParkingHub');
      this.connectionStateSubject.next('connected');
    } catch (err) {
      console.error('[SignalR] Connection failed:', err);
      this.connectionStateSubject.next('disconnected');
      this.isStarted = false;
    }
  }

  private registerEventHandlers(): void {
    if (!this.hubConnection) return;

    // Device status changes (online/offline)
    this.hubConnection.on('DeviceStatusChanged', (data: any) => {
      console.log('[SignalR] DeviceStatusChanged:', data);
      const deviceStatus: JetsonDeviceStatus = {
        hardwareId: data.hardwareId,
        deviceName: data.deviceName,
        online: data.online,
        lastSeen: data.lastSeen ? new Date(data.lastSeen) : null,
        lastIp: data.lastIp,
        tenantId: data.tenantId
      };
      this.deviceStatusChangedSubject.next(deviceStatus);
    });

    // New notifications
    this.hubConnection.on('NewNotification', (data: any) => {
      console.log('[SignalR] NewNotification:', data);
      const notification: NotificationItem = {
        id: data.id,
        description: data.description,
        date: new Date(data.date),
        isReaded: data.isReaded ?? false,
        kind: data.kind,
        externalId: data.externalId
      };
      this.newNotificationSubject.next(notification);
    });

    // New detections
    this.hubConnection.on('NewDetection', (data: any) => {
      console.log('[SignalR] NewDetection:', data);
      const detection: Detection = {
        id: data.id,
        licensePlate: data.licensePlate,
        time: new Date(data.time),
        confidence: data.confidence,
        detectionType: data.detectionType,
        accessResult: data.accessResult,
        plateDetected: data.plateDetected ?? true,
        category: data.category,
        vehicleCategoryId: data.vehicleCategoryId,
        vehicleCategoryName: data.vehicleCategoryName,
        plateImage: data.plateImage,
        evidenceImage: data.evidenceImage,
        vehicleImage: data.vehicleImage,
        cpuUsage: data.cpuUsage,
        gpuUsage: data.gpuUsage,
        ramUsageMb: data.ramUsageMb,
        gpuTemp: data.gpuTemp,
        inferenceMs: data.inferenceMs,
        charConfidences: data.charConfidences ?? null,
      };
      this.newDetectionSubject.next(detection);
    });

    // Camera status changes
    this.hubConnection.on('CameraStatusChanged', (data: any) => {
      const event: CameraStatusEvent = {
        status: data.status,
        statusName: data.statusName,
        tenantId: data.tenantId,
        timestamp: data.timestamp,
        cameraId: data.cameraId,
        errorMessage: data.errorMessage
      };
      this.cameraStatusChangedSubject.next(event);
    });

    this.hubConnection.on('CameraHeartbeat', (data: any) => {
      const event: CameraHeartbeatEvent = {
        cameraId: data.cameraId,
        status: data.status,
        fps: Number(data.fps ?? 0),
        lastFrameAgeMs: Number(data.lastFrameAgeMs ?? 0),
        frameCount: Number(data.frameCount ?? 0),
        droppedFrames: Number(data.droppedFrames ?? 0),
        timestamp: data.timestamp
      };
      this.cameraHeartbeatSubject.next(event);
    });

    this.hubConnection.on('NewParkingIncident', (data: any) => {
      const payload: ParkingIncidentSignalPayload = {
        incidentId: data.incidentId,
        plate: data.plate,
        estimatedAmount: Number(data.estimatedAmount ?? 0),
        incidentType: Number(data.incidentType ?? 0),
        status: Number(data.status ?? 0),
        exitEvidenceImageUrl: data.exitEvidenceImageUrl,
        exitPlateImageUrl: data.exitPlateImageUrl,
        operatorUserId: data.operatorUserId,
        cashBoxOpeningId: data.cashBoxOpeningId,
        isPostClosure: !!data.isPostClosure
      };
      this.newParkingIncidentSubject.next(payload);
    });

    this.hubConnection.on('OccupancyChanged', (data: any) => {
      console.log('[SignalR] OccupancyChanged:', data);
      const event: OccupancyChangedEvent = {
        sessionId: data?.sessionId,
        change: data?.change
      };
      this.occupancyChangedSubject.next(event);
    });
  }
}
